import React, { useRef } from "react";
import {
  DatePicker,
  DownloadableFiles,
  CustomModal,
  GlobalIcon,
  Typography,
  ButtonDS,
} from "get-life-storybook-ts";
import { DownloadableFilesT } from "get-life-storybook-ts/lib/components/DownloadableFiles/DownloadableFiles";
import GuaranteePills from "./GuaranteePills/GuaranteePills";
import { GuaranteeOption } from "./GuaranteeToggles/GuaranteeToggles";
interface DatePickerConfig {
  title: string;
  subtext1: string;
  subtext2: string;
  subtext3?: string;
  textToday?: string;
  textTomorrow?: string;
  textAfterTomorrow?: string;
  initialDate: string;
  onValueChange: (date: Date) => void;
  overlayButtonLabel?: string;
  maxDate: string;
  minDate?: Date;
  locale?: string;
}

interface PolicyCustomizerProps {
  title?: string;
  translate: (key: string) => string;
  changeCapitalOverlay?: React.ReactNode;
  changeCapitalTitle?: React.ReactNode;
  changeCapitalText?: string;
  onChangeCapitalOpen?: () => void;
  datePickerProps?: DatePickerConfig;
  downloadableFiles?: DownloadableFilesT;
  downloadableFilesTitle?: string;
  downloadDocument: (type: string) => void;
  downloadableLoading?: boolean;
  mobileButtonText?: string;
  onMobileButtonClick?: () => void;
  isMobile?: boolean;
  guarantees?: GuaranteeOption[];
  onToggleGuarantee?: (key: string) => void;
  onCapitalChange?: (key: string, capital: number) => void;
}

const PolicyCustomizer: React.FC<PolicyCustomizerProps> = ({
  title,
  translate,
  changeCapitalOverlay,
  changeCapitalTitle,
  changeCapitalText,
  onChangeCapitalOpen,
  datePickerProps,
  downloadableFiles,
  downloadableFilesTitle,
  downloadDocument,
  downloadableLoading,
  mobileButtonText,
  onMobileButtonClick,
  isMobile,
  guarantees,
  onToggleGuarantee,
  onCapitalChange,
}) => {
  const capitalModalRef = useRef<any>(null);

  return (
    <div className="flex flex-col justify-start items-center bg-blue-gray-100 md:bg-white md:min-h-[400px] pt-[32px] md:py-[32px] px-[16px]">
      {title && (
        <Typography
          variant="H3"
          weight="700"
          text={title}
          color="gray-text"
          className="py-[33px]"
        />
      )}

      {guarantees && onToggleGuarantee && guarantees.length > 0 && (
        <div className="w-full flex justify-center pb-[24px]">
          <GuaranteePills
            guarantees={guarantees}
            onToggle={onToggleGuarantee}
            onCapitalChange={onCapitalChange}
            translate={translate}
          />
        </div>
      )}

      {changeCapitalOverlay && changeCapitalText && (
        <div className="py-[30px] max-w-[448px] w-full">
          <button
            type="button"
            onClick={() => {
              onChangeCapitalOpen?.();
              capitalModalRef.current?.handleShowPortal();
            }}
            className="flex items-center gap-2 text-sm font-medium cursor-pointer bg-transparent border-none p-0"
            style={{ color: "var(--theme-primary)" }}
          >
            <GlobalIcon
              iconName="PencilIcon"
              size="XS"
              color="var(--theme-primary)"
            />
            {changeCapitalText}
          </button>
          <CustomModal
            ref={capitalModalRef}
            ModalTitle={changeCapitalTitle}
            ModalBody={changeCapitalOverlay}
          />
        </div>
      )}

      {!isMobile && datePickerProps && (
        <div className="payment-newUpsellContainer__section--datePicker">
          <DatePicker
            title={datePickerProps.title}
            subtext1={datePickerProps.subtext1}
            subtext2={datePickerProps.subtext2}
            subtext3={datePickerProps.subtext3}
            textToday={datePickerProps.textToday}
            textTomorrow={datePickerProps.textTomorrow}
            textAfterTomorrow={datePickerProps.textAfterTomorrow}
            initialDate={new Date(datePickerProps.initialDate)}
            onValueChange={datePickerProps.onValueChange}
            overlayButtonLabel={datePickerProps.overlayButtonLabel}
            maxDate={new Date(datePickerProps.maxDate)}
            minDate={datePickerProps.minDate}
            locale={datePickerProps.locale}
            notShowIpa={true}
          />
        </div>
      )}

      {isMobile && (
        <div className="w-full flex items-center justify-center flex-col pb-[20px]">
          <div className="w-full pt-2 px-4">
            {mobileButtonText && onMobileButtonClick && (
              <ButtonDS
                label={mobileButtonText}
                onClick={onMobileButtonClick}
                className="w-full max-w-[325px] mx-auto"
              />
            )}
            {datePickerProps && (
              <div className="w-full py-[20px]">
                <DatePicker
                  title={datePickerProps.title}
                  subtext1={datePickerProps.subtext1}
                  subtext2={datePickerProps.subtext2}
                  subtext3={datePickerProps.subtext3}
                  textToday={datePickerProps.textToday}
                  textTomorrow={datePickerProps.textTomorrow}
                  textAfterTomorrow={datePickerProps.textAfterTomorrow}
                  initialDate={new Date(datePickerProps.initialDate)}
                  onValueChange={datePickerProps.onValueChange}
                  overlayButtonLabel={datePickerProps.overlayButtonLabel}
                  maxDate={new Date(datePickerProps.maxDate)}
                  minDate={datePickerProps.minDate}
                  locale={datePickerProps.locale}
                  notShowIpa={true}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {downloadableFiles && (
        <div className="py-6 px-4 flex flex-col text-[#424242]">
          {downloadableFilesTitle && (
            <Typography
              text={downloadableFilesTitle}
              variant="H3"
              weight="700"
              className="mb-6 text-center"
            />
          )}
          <DownloadableFiles
            files={downloadableFiles}
            loading={downloadableLoading}
            onClick={downloadDocument}
          />
        </div>
      )}
    </div>
  );
};

export default PolicyCustomizer;
